import BillingsCargoTaxiUpdateParams, {
  BillingsCargoTaxiUpdateParamsBillingContribution,
  BillingsCargoTaxiUpdateParamsBillingContributionType,
  BillingsCargoTaxiUpdateParamsBillingNode,
} from "../../../../../common/services/billings/cargo-taxi/update/billings-cargo-taxi-update-params";
import BillingsNode from "../../../common/types/billings-node";
import BillingsCargoTaxiContributions, {
  BillingsCargoTaxiContributionDiscountType,
} from "../../common/contributions/discount/types/billings-cargo-taxi-contributions-discount";
import BillingFormData, {
  BillingFormDataContributions,
} from "../types/billing-form.data";

const createSaveContributionType = (
  contributionType:
    | BillingsCargoTaxiContributionDiscountType.DISCOUNT_BEING_LATE
    | BillingsCargoTaxiContributionDiscountType.DISCOUNT_INCOMPATIBLE_CAR
    | BillingsCargoTaxiContributionDiscountType.DISCOUNT_OPTIMIZATION
    | BillingsCargoTaxiContributionDiscountType.DISCOUNT_OTHER,
):
  | BillingsCargoTaxiUpdateParamsBillingContributionType.DISCOUNT_BEING_LATE
  | BillingsCargoTaxiUpdateParamsBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR
  | BillingsCargoTaxiUpdateParamsBillingContributionType.DISCOUNT_OPTIMIZATION
  | BillingsCargoTaxiUpdateParamsBillingContributionType.DISCOUNT_OTHER => {
  switch (contributionType) {
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_BEING_LATE:
      return BillingsCargoTaxiUpdateParamsBillingContributionType.DISCOUNT_BEING_LATE;
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_INCOMPATIBLE_CAR:
      return BillingsCargoTaxiUpdateParamsBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR;
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_OPTIMIZATION:
      return BillingsCargoTaxiUpdateParamsBillingContributionType.DISCOUNT_OPTIMIZATION;
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_OTHER:
      return BillingsCargoTaxiUpdateParamsBillingContributionType.DISCOUNT_OTHER;
  }
};

const createBillingDiscountContribution = (
  contribution: BillingsCargoTaxiContributions,
): BillingsCargoTaxiUpdateParamsBillingContribution | undefined => {
  switch (contribution.type) {
    case BillingsCargoTaxiContributionDiscountType.CONTRACT_CORRECTION_PERCENTAGE:
      return {
        type: BillingsCargoTaxiUpdateParamsBillingContributionType.CONTRACT_CORRECTION_PERCENTAGE,
        percentage: contribution.percentage ?? 0,
        amount: contribution.amount ?? 0,
      };
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_BEING_LATE:
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_INCOMPATIBLE_CAR:
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_OPTIMIZATION:
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_OTHER:
      return {
        type: createSaveContributionType(contribution.type),
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    default:
      return undefined;
  }
};

const createBillingContributions = (
  contributions: BillingFormDataContributions,
): BillingsCargoTaxiUpdateParamsBillingContribution[] => {
  const discountContributions = contributions.discount.map(
    (contribution) => createBillingDiscountContribution(contribution)!,
  );

  return [...discountContributions];
};

const createBillingNode = (
  node: BillingsNode,
): BillingsCargoTaxiUpdateParamsBillingNode => {
  return {
    uuid: node.id,
    nodeUuid: node.nodeId,
    orderUuid: node.orderId ?? undefined,
    lat: node.lat,
    lon: node.lon,
    distance: node.distance,
    description: node.description,
    checkoutDate: node.checkoutDate ?? undefined,
    checkoutEventUuid: node.checkoutEventUuid ?? undefined,
    plannedDate: node.plannedDate ?? undefined,
    haltingTime: node.haltingTime,
    allowCharge: node.isHighwayAllowed,
    highwayCharge: node.highwayCharge,
    plannedDistance: node.plannedDistance,
    editableCoordinated: node.isEditable,
  };
};

const createBillingNodes = (
  nodes: BillingsNode[],
): BillingsCargoTaxiUpdateParamsBillingNode[] => {
  return nodes.map(createBillingNode);
};

const createSaveBillingRequest = (
  billingUuid: string,
  data: BillingFormData,
): BillingsCargoTaxiUpdateParams => {
  const result: BillingsCargoTaxiUpdateParams = {
    billingNodes: createBillingNodes(data.billingNodes),
    billingContributions: createBillingContributions(data.billingContributions),
    billingUuid: billingUuid,
  };
  return result;
};

const billingSaveRequestFactory = {
  createSaveBillingRequest,
};

export default billingSaveRequestFactory;
